import {Inject, Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';

import {GoogleAnalyticsEvents, GoogleAnalyticsPaths,} from './model/enum/google-analytics-events';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import {DeviceInfoHelper} from './device-info.helper';
import {isLegacyQuizEvent, translateLegacyQuizEvent} from './legacy-quiz-event-translation';

declare function gtag(eventGA: string): void;
declare function gtag(eventGA: string, eventName: string): void;
declare function gtag(
  eventGA: string,
  eventName: string,
  data: { eventPath?: string; eventTime?: Date; pagePath?: string; page_title?: string }
): void;
declare function gtag(eventGA: string, eventName: string, data: Record<string, unknown>): void;

function isGtagDefined(): boolean {
  return typeof gtag !== 'undefined';
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsEventService {
  readonly googleAnalyticsTrackerId: string;

  constructor(
    private readonly router: Router,
    @Inject(ENV_TOKEN)
    protected readonly environment: EnvInterface) {
    this.googleAnalyticsTrackerId =
      environment.googleAnalytics.trackerId;
  }

  public track(
    eventName: GoogleAnalyticsEvents,
    eventPath: GoogleAnalyticsPaths,
    data: Record<string, string | null>
  ) {
    console.log('googleAnalyticsEventService ', eventName, data);
    if (!isGtagDefined()) return;

    // Normalise keys to lowercase snake_case
    const normalisedData = Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k.toLowerCase(), v])
    );

    gtag('event', eventName, {
      ...normalisedData,
      eventPath: eventPath,
    });
  }

  public configEmitter(event: NavigationEnd) {
    console.log('googleAnalyticsEventService configEmitter ', event.urlAfterRedirects);
    if (!isGtagDefined()) return;
    // Defer so that component lifecycle hooks (e.g. ngAfterViewInit) can set the title first
    setTimeout(() => {
      gtag('config', this.googleAnalyticsTrackerId, {
        pagePath: event.urlAfterRedirects,
        page_title: document.title,
      });
    });
  }

  public isProduction(): boolean {
    return this.environment.app.environment === 'production';
  }

  /**
   * Fired once per video when the viewer has watched ≥10% of the total duration.
   */
  public pushVideoView(productCode: string, subcategoryName: string, videoTitle: string, videoUrl: string) {
    if (!isGtagDefined()) return;
    gtag('event', GoogleAnalyticsEvents.VIDEO_VIEW, {
      product_code: productCode,
      subcategory_name: subcategoryName,
      video_title: videoTitle,
      video_url: videoUrl,
    });
  }

  public pushAudioPlay(title: string, trackName: string, skuId: string) {
    if (!isGtagDefined()) return;
    gtag('event', GoogleAnalyticsEvents.AUDIO_PLAY, {
      title: title,
      track_name: trackName.trim(),
      sku_id: skuId,
    });
  }

  /**
   * Sends an analytics event posted by an embedded quiz client (MixedOnlineTests) to GA4, with the
   * viewer's device added. The client names the event and its parameters in GA4 form, so it is sent
   * on as it is. Events from clients built before that change are translated first.
   *
   * Sent with gtag: a plain-object dataLayer push is read by gtag.js but never reached GA4.
   */
  public pushIframeAnalyticsEvent(analyticsEvent: Record<string, unknown>): void {
    // TEMPORARY: see legacy-quiz-event-translation.ts for when to remove this.
    const ga4Event = isLegacyQuizEvent(analyticsEvent) ? translateLegacyQuizEvent(analyticsEvent) : analyticsEvent;

    const {event, ...fields} = {...ga4Event, ...GoogleAnalyticsEventService.deviceParams()};

    if (!this.isProduction()) {
      console.log('[analyticsEvent from iframe]', event, fields);
    }

    if (typeof event !== 'string' || !GoogleAnalyticsEventService.GA4_EVENT_NAME.test(event)) {
      console.warn('Ignoring iframe analytics event with no valid GA4 name:', event);
      return;
    }

    if (!isGtagDefined()) return;
    gtag('event', event, GoogleAnalyticsEventService.toGa4Params(fields));
  }

  /**
   * Forwards the analytics events an embedded quiz client posts to this window (a message with
   * eventName 'analyticsEvent') to GA4, with the host's parameters added. Returns a function that
   * stops listening, for the host's ngOnDestroy.
   */
  public listenForIframeAnalyticsEvents(getHostParams: () => Record<string, unknown>): () => void {
    const listener = (event: MessageEvent) => {
      const analyticsEvent = GoogleAnalyticsEventService.parseIframeAnalyticsMessage(event.data);
      if (analyticsEvent) {
        this.pushIframeAnalyticsEvent({...analyticsEvent, ...getHostParams()});
      }
    };

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }

  /** The event in a quiz client's analyticsEvent message, or null for any other message. */
  static parseIframeAnalyticsMessage(raw: unknown): Record<string, unknown> | null {
    let data: any;
    try {
      data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return null;
    }

    if (data?.eventName !== 'analyticsEvent') return null;

    try {
      const analyticsEvent = typeof data.analyticsEvent === 'string'
        ? JSON.parse(data.analyticsEvent)
        : data.analyticsEvent;
      return analyticsEvent && typeof analyticsEvent === 'object' ? analyticsEvent : null;
    } catch {
      return null;
    }
  }

  // GA4 event names: a letter, then letters, numbers and underscores, up to 40 characters
  private static readonly GA4_EVENT_NAME = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;

  /** GA4 parameters are scalars, so arrays, objects and empty values are dropped. */
  static toGa4Params(fields: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(fields).filter(([, value]) => value !== null && value !== undefined && typeof value !== 'object')
    );
  }

  // The parsed device fields only: the raw user agent is long and adds nothing they don't
  private static deviceParams(): Record<string, unknown> {
    const device = DeviceInfoHelper.getDeviceInfo();
    return {
      device_type: device.deviceType,
      device_model: device.deviceModel,
      os: device.os,
      browser: device.browser,
    };
  }

  public setUpAnalytics() {
    //console.log('googleAnalyticsEventService setUpAnalytics');
    const waitForGtag = (attempt = 0) => {
      if (isGtagDefined()) {
        //console.log('gtag event setUpAnalytics ', attempt);
        this.router.events.subscribe((event) => {
          if (event instanceof NavigationEnd) {
            this.configEmitter(event);
          }
        });
      } else if (attempt < 20) {
        setTimeout(() => waitForGtag(attempt + 1), 250);
      }
    };
    waitForGtag();
  }
}
